import { Alert, AlertIcon, Button, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiExternalLink } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { useGetNeedForProcurementQuery, type SourcingEventDto } from "../../../../../autogen/bff-api";
import { useCpvCodes } from "../../../../../common/cpv/useCpvCodes";
import { urls } from "../../../../../urls";
import { distinguishableName } from "../../../../../common/documents/attachmentDetails";

interface Props {
  event: SourcingEventDto;
}

export const NeedOriginNotice = ({ event }: Props) => {
  const { t, i18n } = useTranslation();
  const { data } = useGetNeedForProcurementQuery({ sourcingEventId: event.id });
  const { codeIndex } = useCpvCodes();
  const need = data?.need;
  if (!need) return null;

  const inProcurement = new Set((event.eventData.documents ?? []).map((document) => document.fromUploadId));
  const leftOnNeed = (need.documents ?? []).filter((document) => !inProcurement.has(document.fromUploadId));
  const cpvCodes = need.procurerAssessment?.cpvCodes ?? [];
  const cpvNotCarried = event.eventContext.type === "Private" && cpvCodes.length > 0;
  const isNorwegian = i18n.language.startsWith("no");
  const cpvLabel = (code: string) => {
    const dto = codeIndex[code];
    const name = dto ? (isNorwegian ? dto.nameNb : dto.nameEn) : "";
    return name ? `${code} — ${name}` : code;
  };

  return (
    <Alert status="info" borderRadius="md" mt="4" alignItems="flex-start" data-testid="need-origin-notice">
      <AlertIcon />
      <Stack spacing="2" flex="1" minW="0" fontSize="sm">
        <Text>{t("Created from the need «{{title}}».", { title: need.title?.trim() || t("Untitled") })}</Text>
        {leftOnNeed.length > 0 && (
          <Text>
            {t("{{count}} of the need's attachments are not in this procurement. They stay on the need: {{names}}.", {
              count: leftOnNeed.length,
              names: leftOnNeed.map((document) => distinguishableName(document, need.documents ?? [])).join(", "),
            })}
          </Text>
        )}
        {cpvNotCarried && (
          <Text>
            {t(
              "The need's CPV codes are not used here, because a procurement below the threshold has none. They stay on the need: {{codes}}.",
              { codes: cpvCodes.map(cpvLabel).join(", "), count: cpvCodes.length },
            )}
          </Text>
        )}
        <Button
          as={RouterLink}
          to={urls.app.needs.view.go(need.id)}
          target="_blank"
          variant="link"
          size="sm"
          alignSelf="flex-start"
          colorScheme="teal"
          rightIcon={<Icon as={FiExternalLink} />}
        >
          {t("Open the need")}
        </Button>
      </Stack>
    </Alert>
  );
};
